import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Sensitive Data Guard Service
 * 
 * Applies additional protections when user is on admin/sensitive pages:
 * - Disables copy/paste of sensitive data
 * - Adds CSS to prevent screenshots of sensitive areas
 * - Blurs content when tab loses focus (prevents shoulder surfing)
 * - Detects Print Screen attempts
 */
@Injectable({
  providedIn: 'root'
})
export class SensitiveDataGuardService {

  private readonly SENSITIVE_ROUTES = [
    '/admin',
    '/super-admin',
    '/backoffice'
  ];

  private isOnSensitivePage = false;
  private blurOverlay: HTMLElement | null = null;

  constructor(private router: Router) {
    this.monitorNavigation();
    this.setupVisibilityGuard();
    this.setupCopyProtection();
    this.setupPrintProtection();
  }

  private monitorNavigation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      const navEvent = event as NavigationEnd;
      const url = navEvent.urlAfterRedirects || navEvent.url;
      this.isOnSensitivePage = this.SENSITIVE_ROUTES.some(route => url.startsWith(route));

      // Apply/remove protections based on current page
      if (this.isOnSensitivePage) {
        this.applySensitiveProtections();
      } else {
        this.removeSensitiveProtections();
      }
    });
  }

  /**
   * Blur page content when tab is hidden or loses focus.
   * Prevents data capture via tab switching tools.
   */
  private setupVisibilityGuard(): void {
    document.addEventListener('visibilitychange', () => {
      if (!this.isOnSensitivePage) return;

      if (document.hidden) {
        this.showBlurOverlay();
      } else {
        this.hideBlurOverlay();
      }
    });

    window.addEventListener('blur', () => {
      if (!this.isOnSensitivePage) return;
      this.showBlurOverlay();
    });

    window.addEventListener('focus', () => {
      this.hideBlurOverlay();
    });
  }

  /**
   * Prevent copy/cut of content on sensitive pages.
   */
  private setupCopyProtection(): void {
    document.addEventListener('copy', (e) => {
      if (this.isOnSensitivePage) {
        e.preventDefault();
        // Replace clipboard with a warning
        e.clipboardData?.setData('text/plain', '[Content protected - copying not allowed on admin pages]');
      }
    });

    document.addEventListener('cut', (e) => {
      if (this.isOnSensitivePage) {
        e.preventDefault();
      }
    });
  }

  /**
   * Detect Print Screen and Ctrl+P attempts.
   */
  private setupPrintProtection(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.isOnSensitivePage) return;

      // PrintScreen key
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        this.flashProtectionOverlay();
      }

      // Ctrl+P (Print)
      if (e.ctrlKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
      }
    });

    // Detect window.print() calls via beforeprint event
    window.addEventListener('beforeprint', (e) => {
      if (this.isOnSensitivePage) {
        // Hide sensitive content during print
        document.body.classList.add('print-protected');
      }
    });

    window.addEventListener('afterprint', () => {
      document.body.classList.remove('print-protected');
    });
  }

  private applySensitiveProtections(): void {
    // Add CSS class that disables text selection on sensitive elements
    document.body.classList.add('sensitive-page');
  }

  private removeSensitiveProtections(): void {
    document.body.classList.remove('sensitive-page');
    this.hideBlurOverlay();
  }

  private showBlurOverlay(): void {
    if (this.blurOverlay) return;

    this.blurOverlay = document.createElement('div');
    this.blurOverlay.id = 'security-blur-overlay';
    this.blurOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(20px);
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: sans-serif;
      font-size: 18px;
      color: #333;
    `;
    this.blurOverlay.innerHTML = '<div>🔒 Content hidden for security. Click to view.</div>';
    this.blurOverlay.addEventListener('click', () => this.hideBlurOverlay());
    document.body.appendChild(this.blurOverlay);
  }

  private hideBlurOverlay(): void {
    if (this.blurOverlay) {
      this.blurOverlay.remove();
      this.blurOverlay = null;
    }
  }

  private flashProtectionOverlay(): void {
    const flash = document.createElement('div');
    flash.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: white;
      z-index: 99999;
    `;
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), 200);
  }
}
